import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { productService } from '../services/productService';
import { marginService } from '../services/marginService';
import depositService from '../services/depositService';
import priceLimitsService from '../services/priceLimitsService';
import {
  isZeroPricedProduct,
  isZeroPricedCartItem,
  isDepositLikeItem,
  itemLabel,
  ZERO_PRICE_BLOCK_MESSAGE,
  ZERO_PRICE_WARNING_MESSAGE,
} from '../contexts/CartContext';

/**
 * Hook do operacji na koszyku (addToCart, updatePrice, validatePriceLimit)
 */
const useCartOperations = ({
  cart,
  setCart,
  currentShift,
  setShowCloseShiftModal,
  documentType,
  selectedCustomer,
  priceLimitSettings,
  setPriceLimitWarnings,
  currentLocationId
}) => {

  /**
   * Walidacja ceny wzgledem granicy marzy
   */
  const validatePriceLimit = useCallback(async (productId, proposedPrice, purchasePrice) => {
    if (!priceLimitSettings || !priceLimitSettings.enabled) {
      return { valid: true };
    }

    try {
      const response = await priceLimitsService.validatePrice(
        currentLocationId,
        productId,
        proposedPrice,
        purchasePrice
      );
      return response.data || { valid: true };
    } catch (error) {
      console.error("Blad walidacji ceny:", error);
      return { valid: true }; // W przypadku bledu pozwalamy na sprzedaz
    }
  }, [priceLimitSettings, currentLocationId]);

  /**
   * Dodanie produktu do koszyka
   */
  const addToCart = useCallback(async (product, isDeposit = false) => {
    // Blokada - nie mozna dodawac produktow bez otwartej zmiany
    if (!currentShift) {
      toast.error('Nie mozna dodac produktu - najpierw otworz zmiane!');
      return;
    }

    // Blokada - nie mozna dodawac produktow gdy zmiana jest przeterminowana
    if (currentShift?.is_expired) {
      toast.error(`Zmiana z dnia ${currentShift.data_zmiany} jest przeterminowana! Zamknij stara zmiane i otworz nowa przed dokonaniem sprzedazy.`);
      setShowCloseShiftModal(true);
      return;
    }

    // Blokada - towar bez ceny (dokladnie 0.00) nie ma prawa trafic do koszyka.
    // Decyzja biznesowa wlasciciela (2026-08): towar z cena 0.00 nie wchodzi
    // do koszyka, minimum 0.01 zl. Protokol XML (pxml.txt) NIE stawia warunku
    // > 0 - to nie jest wymog drukarki. Kaucje (takze 0.00) sa wylaczone
    // z blokady, pozycje za 0.01 sa dozwolone.
    if (!isDeposit && !isDepositLikeItem(product)) {
      // Jesli towar juz jest w koszyku, patrzymy na jego aktualna (byc moze
      // recznie poprawiona) cene, a nie na zero z kartoteki.
      const existingForPrice = cart.find(item => item.id === product.id && !item.is_deposit);
      const zeroPrice = existingForPrice
        ? isZeroPricedCartItem(existingForPrice)
        : isZeroPricedProduct(product);

      if (zeroPrice) {
        toast.error(`${itemLabel(product)}: ${ZERO_PRICE_BLOCK_MESSAGE}`);
        return;
      }
    }

    // Ostrzezenie dla "Paragon z NIP" - wymagany klient z NIP
    if (documentType === 'paragon_nip' && cart.length === 0) {
      const nipValue = selectedCustomer?.nip;
      const hasNip = nipValue && typeof nipValue === 'string' && nipValue.trim() !== '';
      if (!selectedCustomer || !hasNip) {
        const proceed = window.confirm(
          'PARAGON Z NIP\n\n' +
          'Wybrales typ dokumentu "Paragon z NIP" ktory wymaga klienta z NIP.\n\n' +
          (selectedCustomer
            ? `Wybrany klient "${selectedCustomer.nazwa_firmy || selectedCustomer.imie}" nie ma NIP!\n\n`
            : 'Nie wybrano jeszcze klienta!\n\n') +
          'Wybierz klienta z NIP przed dodaniem produktow.\n\n' +
          'Kliknij OK aby kontynuowac mimo to, lub Anuluj aby najpierw wybrac klienta.'
        );
        if (!proceed) {
          return;
        }
      }
    }

    // Pre-fetch deposit info ZAWSZE przed setCart (race-free).
    // Dzieki temu cala logika cart-update mozna zrobic synchronicznie w jednym
    // setCart callbacku z prev state - nawet 9 szybkich klikow nie pogubi kaucji.
    let depositInfo = null;
    if (!isDeposit && product.id) {
      try {
        const depositResult = await depositService.checkProductDeposit(product.id);
        if (depositResult.success && depositResult.hasDeposit && depositResult.deposit) {
          depositInfo = depositResult.deposit;
        } else if (depositResult.failed) {
          // Sprawdzenie kaucji padlo (np. brak uprawnien/sieci) - kasjer MUSI o tym
          // wiedziec, bo inaczej kaucja po cichu nie trafi na paragon.
          toast.error('Nie udalo sie sprawdzic kaucji - dodaj ja recznie, jesli produkt jej wymaga!');
        }
      } catch (err) {
        console.error('POS: Blad sprawdzania kaucji:', err);
        toast.error('Nie udalo sie sprawdzic kaucji - dodaj ja recznie, jesli produkt jej wymaga!');
      }
    }

    const depositData = depositInfo ? {
      kaucja_product_id: depositInfo.produkt_id || null,
      kaucja_price: depositInfo.kwota,
      kaucja_id: depositInfo.id,
      kaucja_nazwa: depositInfo.produkt_nazwa || depositInfo.nazwa,
    } : null;

    const buildDepositItem = () => ({
      id: depositInfo.produkt_id || `deposit_${depositInfo.id}`,
      name: depositInfo.produkt_nazwa || `Kaucja: ${depositInfo.nazwa}`,
      nazwa: depositInfo.produkt_nazwa || `Kaucja: ${depositInfo.nazwa}`,
      price: depositInfo.kwota,
      cena: depositInfo.kwota,
      default_price_brutto: depositInfo.kwota,
      stawka_vat: -2,
      vat: -2,
      typ_produktu: 'kaucja',
      is_deposit: true,
      deposit_id: depositInfo.id,
      deposit_for_product_id: product.id,
      quantity: 1,
    });

    // Sprawdz przez prev (nie cart z closure) zeby uniknac race
    const existingItem = cart.find(item => item.id === product.id && !item.is_deposit);

    if (existingItem) {
      // Caly update WEWNATRZ jednego setCart callbacku z prev state.
      setCart(prev => {
        let depositExists = false;
        const updated = prev.map(item => {
          if (item.id === product.id && !item.is_deposit) {
            return {
              ...item,
              quantity: item.quantity + 1,
              ...(depositData ? { deposit_data: depositData } : {}),
            };
          }
          if (depositInfo && item.is_deposit && item.deposit_for_product_id === product.id) {
            depositExists = true;
            return { ...item, quantity: item.quantity + 1 };
          }
          return item;
        });
        // Brak istniejacej kaucji dla tego rodzica - dodaj nowa
        if (depositInfo && !depositExists) {
          updated.push(buildDepositItem());
        }
        return updated;
      });
    } else {
      // Uzyj ceny specjalnej jesli istnieje
      const rawPrice = product.has_special_price && product.special_price_brutto
        ? product.special_price_brutto
        : product.default_price_brutto || product.price || product.cena || 0;
      const finalPrice = Math.round(parseFloat(rawPrice) * 100) / 100;

      const productVatRate = product.stawka_vat ?? 23;

      // Walidacja granicy ceny przy dodawaniu do koszyka
      if (priceLimitSettings && priceLimitSettings.enabled && !isDeposit) {
        const purchasePrice = product.purchase_price || product.cena_zakupu || 0;

        if (purchasePrice > 0) {
          const validation = await validatePriceLimit(product.id, finalPrice, purchasePrice);

          if (!validation.valid) {
            setPriceLimitWarnings(prev => ({
              ...prev,
              [product.id]: {
                message: validation.message,
                min_price: validation.min_price,
                current_margin: validation.current_margin_percent,
                min_margin: validation.min_margin_percent
              }
            }));

            if (!validation.allow_override) {
              toast.error(`GRANICA CENY! ${product.name || product.nazwa} - ${validation.message} Produkt nie zostal dodany do koszyka.`);
              return;
            } else {
              const proceed = window.confirm(
                `OSTRZEZENIE: Granica ceny!\n\n${product.name || product.nazwa}\n\n${validation.message}\n\n` +
                `Czy na pewno chcesz dodac ten produkt? (wymaga uprawnien administratora)`
              );
              if (!proceed) return;
            }
          }
        }
      }

      const newCartItem = {
        ...product,
        quantity: 1,
        price: finalPrice,
        name: product.name || product.nazwa,
        has_special_price: product.has_special_price || false,
        special_price_brutto: product.special_price_brutto || null,
        default_price_brutto: product.default_price_brutto || product.price || product.cena || 0,
        current_purchase_price: product.purchase_price || product.cena_zakupu || 0,
        purchase_price_method: product.purchase_price_method || 'Tabela produkty',
        margin_percent: null,
        margin_amount: null,
        fifo_purchase_price: null,
        fifo_margin_percent: null,
        fifo_margin_amount: null,
        available_stock: product.stock_quantity ?? product.stan_magazynowy ?? product.available_stock ?? 0,
        is_deposit: isDeposit || false,
        stawka_vat: productVatRate,
        ...(depositData ? { deposit_data: depositData } : {}),
      };

      // Race-free: jeden setCart callback ktory dodaje produkt + opcjonalna kaucja.
      // Jesli prev juz ma ten produkt (np. szybki click w trakcie await),
      // zwiekszamy qty zamiast dodawac duplikat.
      setCart(prev => {
        const existsAlready = prev.find(it => it.id === product.id && !it.is_deposit);
        if (existsAlready) {
          let depositExists = false;
          const updated = prev.map(item => {
            if (item.id === product.id && !item.is_deposit) {
              return {
                ...item,
                quantity: item.quantity + 1,
                ...(depositData ? { deposit_data: depositData } : {}),
              };
            }
            if (depositInfo && item.is_deposit && item.deposit_for_product_id === product.id) {
              depositExists = true;
              return { ...item, quantity: item.quantity + 1 };
            }
            return item;
          });
          if (depositInfo && !depositExists) {
            updated.push(buildDepositItem());
          }
          return updated;
        }
        // Faktycznie nowy produkt - dodaj rodzica + opcjonalna kaucje
        const items = [...prev, newCartItem];
        if (depositInfo) items.push(buildDepositItem());
        return items;
      });

      // Oblicz marze dla nowo dodanego produktu
      if (product.id && finalPrice > 0) {
        marginService.calculateMargin({
          product_id: product.id,
          sell_price_brutto: finalPrice,
          warehouse_id: currentLocationId
        }).then(marginResult => {
          setCart(prevCart =>
            prevCart.map((item) => {
              if (item.id === product.id && item.margin_percent === null) {
                return {
                  ...item,
                  margin_percent: marginResult.margin_percent,
                  margin_amount: marginResult.margin_amount,
                  current_purchase_price: marginResult.purchase_price,
                  purchase_price_method: marginResult.purchase_price_method,
                  fifo_purchase_price: marginResult.fifo_purchase_price || null,
                  fifo_margin_percent: marginResult.fifo_margin_percent || null,
                  fifo_margin_amount: marginResult.fifo_margin_amount || null,
                };
              }
              return item;
            })
          );
        }).catch(error => {
          console.error('POS: Blad obliczania marzy dla nowego produktu:', error);
        });
      }

      // Kaucja juz dodana w setCart powyzej (race-free, przez pre-fetched depositInfo).
    }
  }, [cart, setCart, currentShift, setShowCloseShiftModal, documentType, selectedCustomer,
      priceLimitSettings, setPriceLimitWarnings, currentLocationId, validatePriceLimit]);

  /**
   * Aktualizacja ceny produktu w koszyku
   */
  const updatePrice = useCallback(async (productId, newPrice, isNetto = false) => {
    const item = cart.find(i => i.id === productId);
    if (!item) return;

    const vat = item.stawka_vat || 23;
    const vatMultiplier = 1 + vat / 100;

    let updatedPrice;
    if (isNetto) {
      updatedPrice = Math.round(newPrice * vatMultiplier * 100) / 100;
    } else {
      updatedPrice = Math.round(newPrice * 100) / 100;
    }

    // TWARDA GRANICA CENY - NIEZALEZNA od priceLimitSettings.enabled.
    // Decyzja biznesowa wlasciciela (2026-08): pozycja nie moze kosztowac
    // mniej niz 0.01 zl (gratis/probka/promocja zawsze minimum 0.01 zl).
    // Protokol XML (pxml.txt) NIE stawia warunku > 0 - to nie wymog drukarki.
    // Kaucje: 0.00 jest legalne, ujemna cena nie - przycinamy do zera.
    //
    // Cena < 0.01 dla pozycji nie-kaucyjnej jest ODRZUCANA (koszyk zachowuje
    // poprzednia poprawna cene), a wiersz dostaje widoczne ostrzezenie.
    // Celowo bez toastu: onChange wola updatePrice przy kazdym klawiszu
    // (wpisanie "0.5" przechodzi przez "0"), toast sypalby sie w trakcie
    // normalnego wpisywania ceny.
    if (!isDepositLikeItem(item)) {
      if (!(updatedPrice >= 0.01)) {
        setPriceLimitWarnings(prev => ({
          ...prev,
          [productId]: { message: ZERO_PRICE_WARNING_MESSAGE, zero_price: true }
        }));
        return; // cena NIE zostaje zastosowana
      }
    } else if (updatedPrice < 0) {
      updatedPrice = 0;
    }

    // Walidacja granicy ceny
    if (priceLimitSettings && priceLimitSettings.enabled) {
      const purchasePrice = item.current_purchase_price || item.cena_zakupu || 0;

      if (purchasePrice > 0) {
        const validation = await validatePriceLimit(productId, updatedPrice, purchasePrice);

        if (!validation.valid) {
          setPriceLimitWarnings(prev => ({
            ...prev,
            [productId]: {
              message: `Cena ponizej min. marzy ${validation.min_margin_percent}%. Min: ${validation.min_price?.toFixed(2)} zl`,
              min_price: validation.min_price,
              min_margin: validation.min_margin_percent
            }
          }));
        } else {
          setPriceLimitWarnings(prev => {
            const newWarnings = { ...prev };
            delete newWarnings[productId];
            return newWarnings;
          });
        }
      }
    }

    // Cena przeszla twarda granice (>= 0.01) - zgas ewentualne ostrzezenie
    // o zerze z poprzedniej proby. Kasujemy TYLKO wlasne ostrzezenie o zerze,
    // zeby nie zdjac ostrzezenia o granicy marzy ustawionego wyzej.
    if (!isDepositLikeItem(item)) {
      setPriceLimitWarnings(prev => {
        if (!prev[productId] || !prev[productId].zero_price) return prev;
        const newWarnings = { ...prev };
        delete newWarnings[productId];
        return newWarnings;
      });
    }

    // Aktualizuj koszyk
    setCart(prev =>
      prev.map((cartItem) => {
        if (cartItem.id === productId) {
          return { ...cartItem, price: updatedPrice };
        }
        return cartItem;
      })
    );

    // Oblicz nowa marze (uzyj updatedPrice bezposrednio)
    if (item) {
      try {
        const marginResult = await marginService.calculateMargin({
          product_id: productId,
          sell_price_brutto: updatedPrice,
          warehouse_id: currentLocationId
        });

        setCart(prevCart =>
          prevCart.map((cartItem) => {
            if (cartItem.id === productId) {
              return {
                ...cartItem,
                margin_percent: marginResult.margin_percent,
                margin_amount: marginResult.margin_amount,
                current_purchase_price: marginResult.purchase_price,
                purchase_price_method: marginResult.purchase_price_method,
                fifo_purchase_price: marginResult.fifo_purchase_price || null,
                fifo_margin_percent: marginResult.fifo_margin_percent || null,
                fifo_margin_amount: marginResult.fifo_margin_amount || null,
              };
            }
            return cartItem;
          })
        );

      } catch (error) {
        console.error('POS: Blad obliczania marzy:', error);
      }
    }
  }, [cart, setCart, priceLimitSettings, setPriceLimitWarnings, currentLocationId, validatePriceLimit]);

  return {
    addToCart,
    updatePrice,
    validatePriceLimit
  };
};

export default useCartOperations;
